import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Image,
  Linking,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useRouter, useLocalSearchParams } from "expo-router";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import { Profile, Team } from "@/types";

/**
 * User profile with team info
 */
interface UserProfileData {
  profile: Profile;
  team: (Team & { is_captain: boolean }) | null;
  personalScores: {
    scoresAwarded: number;
    totalPointsContributed: number;
  };
}

/**
 * Stat card component
 */
interface StatCardProps {
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  iconBgColor: string;
  value: string | number;
  label: string;
}

const StatCard = ({ icon, iconColor, iconBgColor, value, label }: StatCardProps) => {
  const colors = useThemeColors();

  return (
    <View
      className="flex-1 rounded-2xl p-4 items-center"
      style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
    >
      <View
        className="w-12 h-12 rounded-full items-center justify-center mb-2"
        style={{ backgroundColor: iconBgColor }}
      >
        <Ionicons name={icon} size={24} color={iconColor} />
      </View>
      <Text className="text-2xl font-bold" style={{ color: colors.text }}>{value}</Text>
      <Text className="text-xs text-center mt-0.5" style={{ color: colors.textSecondary }}>{label}</Text>
    </View>
  );
};

/**
 * Info row component
 */
interface InfoRowProps {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  value: string;
  onPress?: () => void;
}

const InfoRow = ({ icon, label, value, onPress }: InfoRowProps) => {
  const colors = useThemeColors();

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      className={`flex-row items-center py-3 ${onPress ? "active:opacity-70" : ""}`}
      accessibilityRole={onPress ? "button" : "text"}
      accessibilityLabel={`${label}: ${value}`}
    >
      <View
        className="w-10 h-10 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: colors.surfaceSecondary }}
      >
        <Ionicons name={icon} size={18} color={colors.textSecondary} />
      </View>
      <View className="flex-1">
        <Text className="text-xs uppercase tracking-wide" style={{ color: colors.textTertiary }}>
          {label}
        </Text>
        <Text className="font-medium" style={{ color: colors.text }}>{value}</Text>
      </View>
      {onPress && <Ionicons name="chevron-forward" size={18} color={colors.textTertiary} />}
    </Pressable>
  );
};

/**
 * User Profile View Screen
 * Displays another user's profile with their team and stats
 */
export default function UserProfileScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { activeProfile, activeWeekend } = useAuth();
  const { isDark } = useTheme();
  const colors = useThemeColors();

  const [userData, setUserData] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Check if viewing own profile (could be main user or switched dependent)
  const isOwnProfile = activeProfile?.id === id;

  /**
   * Fetch user profile data
   */
  const fetchUserData = useCallback(async () => {
    if (!id) return;

    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", id)
        .single();

      if (profileError) throw profileError;

      // Fetch team membership for active weekend
      let teamData = null;
      if (activeWeekend) {
        const { data: membershipData } = await supabase
          .from("team_members")
          .select(`
            is_captain,
            teams:team_id (
              id,
              name,
              color,
              description,
              avatar_url,
              total_score,
              weekend_id,
              created_at
            )
          `)
          .eq("user_id", id)
          .eq("teams.weekend_id", activeWeekend.id)
          .single();

        if (membershipData?.teams) {
          teamData = {
            ...(membershipData.teams as unknown as Team),
            is_captain: membershipData.is_captain,
          };
        }
      }

      // Fetch personal score contributions from score_history
      let personalScores = {
        scoresAwarded: 0,
        totalPointsContributed: 0,
      };

      // Get score history where this user was awarded points (awarded_to)
      const { data: scoreData } = await supabase
        .from("score_history")
        .select("points")
        .eq("awarded_to", id);

      if (scoreData && scoreData.length > 0) {
        personalScores.scoresAwarded = scoreData.length;
        personalScores.totalPointsContributed = scoreData.reduce(
          (sum, s) => sum + (s.points || 0), 0
        );
      }

      setUserData({
        profile: profileData,
        team: teamData,
        personalScores,
      });
    } catch (error) {
      console.error("Error fetching user data:", error);
      Alert.alert("Fout", "Kon profiel niet laden");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [id, activeWeekend]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  /**
   * Handle refresh
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchUserData();
  };

  /**
   * Handle start chat with user
   */
  const handleStartChat = () => {
    // TODO: Implement chat functionality
    Alert.alert(
      "Binnenkort beschikbaar",
      `Chat met ${userData?.profile.full_name} wordt binnenkort toegevoegd!`
    );
  };

  /**
   * Handle call user (if phone available)
   */
  const handleCall = () => {
    if (userData?.profile.phone) {
      Linking.openURL(`tel:${userData.profile.phone}`);
    }
  };

  /**
   * Handle email user
   */
  const handleEmail = () => {
    if (userData?.profile.email) {
      Linking.openURL(`mailto:${userData.profile.email}`);
    }
  };

  /**
   * Navigate to team details
   */
  const handleViewTeam = () => {
    if (userData?.team) {
      router.push(`/(tabs)/teams/${userData.team.id}` as any);
    }
  };

  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-2" style={{ color: colors.textSecondary }}>Profiel laden...</Text>
      </SafeAreaView>
    );
  }

  if (!userData) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center px-8" style={{ backgroundColor: colors.background }}>
        <Ionicons name="person-outline" size={64} color={colors.textTertiary} />
        <Text className="text-xl font-semibold mt-4" style={{ color: colors.text }}>
          Gebruiker niet gevonden
        </Text>
        <Text className="text-center mt-2" style={{ color: colors.textSecondary }}>
          Dit profiel bestaat niet of is niet meer beschikbaar.
        </Text>
        <Pressable
          onPress={() => router.back()}
          className="mt-6 rounded-xl px-6 py-3"
          style={{ backgroundColor: colors.primary }}
          accessibilityRole="button"
          accessibilityLabel="Terug"
        >
          <Text className="text-white font-semibold">Terug</Text>
        </Pressable>
      </SafeAreaView>
    );
  }

  const { profile, team, personalScores } = userData;
  const teamColor = team?.color || "#7C3AED";
  const initials = profile.full_name
    ?.split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top"]}>
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
      >
        {/* Header */}
        <View className="flex-row items-center px-4 py-3">
          <Pressable
            onPress={() => router.back()}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
            accessibilityRole="button"
            accessibilityLabel="Terug"
          >
            <Ionicons name="arrow-back" size={20} color={colors.text} />
          </Pressable>
          <Text className="flex-1 text-center text-lg font-semibold mr-10" style={{ color: colors.text }}>
            Profiel
          </Text>
        </View>

        {/* Profile Hero Section */}
        <View className="items-center px-6 pt-4 pb-6">
          {/* Avatar */}
          <View
            className="w-28 h-28 rounded-full items-center justify-center mb-4 border-4 overflow-hidden"
            style={{
              backgroundColor: team ? `${teamColor}15` : colors.surfaceSecondary,
              borderColor: team ? teamColor : colors.border,
            }}
          >
            {profile.avatar_url ? (
              <Image
                source={{ uri: profile.avatar_url }}
                className="w-full h-full"
                resizeMode="cover"
              />
            ) : (
              <Text
                className="text-4xl font-bold"
                style={{ color: team ? teamColor : colors.textSecondary }}
              >
                {initials}
              </Text>
            )}
          </View>

          {/* Name */}
          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            {profile.full_name || "Onbekend"}
          </Text>

          {/* Nickname if available */}
          {profile.nickname && (
            <Text className="italic mt-0.5" style={{ color: colors.textSecondary }}>"{profile.nickname}"</Text>
          )}

          {/* Team Badge */}
          {team && (
            <Pressable
              onPress={handleViewTeam}
              className="flex-row items-center mt-3 px-4 py-2 rounded-full active:opacity-80"
              style={{ backgroundColor: `${teamColor}15` }}
              accessibilityRole="button"
              accessibilityLabel={`Bekijk team ${team.name}`}
            >
              <View
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: teamColor }}
              />
              <Text className="font-semibold" style={{ color: teamColor }}>
                {team.name}
              </Text>
              {team.is_captain && (
                <View className="ml-2 bg-amber-400 rounded-full px-2 py-0.5">
                  <Text className="text-xs font-bold text-amber-900">👑 Aanvoerder</Text>
                </View>
              )}
              <Ionicons name="chevron-forward" size={16} color={teamColor} />
            </Pressable>
          )}

          {/* Own profile badge */}
          {isOwnProfile && (
            <View className="mt-4 rounded-full px-4 py-2" style={{ backgroundColor: colors.primaryLight }}>
              <Text className="font-medium" style={{ color: colors.primary }}>
                Dit is jouw profiel ✨
              </Text>
            </View>
          )}
        </View>

        {/* Stats Section */}
        <View className="px-4 mb-6">
          <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textSecondary }}>
            Statistieken
          </Text>
          <View className="flex-row gap-3">
            <StatCard
              icon="trophy"
              iconColor="#F59E0B"
              iconBgColor={isDark ? "#78350F" : "#FEF3C7"}
              value={team?.total_score || 0}
              label="Team Score"
            />
            <StatCard
              icon="star"
              iconColor="#8B5CF6"
              iconBgColor={isDark ? "#4C1D95" : "#EDE9FE"}
              value={personalScores.totalPointsContributed}
              label="Punten Bijgedragen"
            />
            <StatCard
              icon="ribbon"
              iconColor="#10B981"
              iconBgColor={isDark ? "#064E3B" : "#D1FAE5"}
              value={personalScores.scoresAwarded}
              label="Prestaties"
            />
          </View>
        </View>

        {/* Team Card */}
        {team && (
          <View className="px-4 mb-6">
            <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textSecondary }}>
              Team
            </Text>
            <Pressable
              onPress={handleViewTeam}
              className="rounded-2xl p-4 flex-row items-center"
              style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
              accessibilityRole="button"
              accessibilityLabel={`Bekijk team ${team.name}`}
            >
              {/* Team Avatar */}
              <View
                className="w-14 h-14 rounded-xl items-center justify-center mr-4 overflow-hidden"
                style={{ backgroundColor: `${teamColor}15` }}
              >
                {team.avatar_url?.startsWith("http") ? (
                  <Image
                    source={{ uri: team.avatar_url }}
                    className="w-full h-full"
                    resizeMode="cover"
                  />
                ) : team.avatar_url ? (
                  <Text className="text-3xl">{team.avatar_url}</Text>
                ) : (
                  <Text
                    className="text-2xl font-bold"
                    style={{ color: teamColor }}
                  >
                    {team.name.charAt(0)}
                  </Text>
                )}
              </View>

              {/* Team Info */}
              <View className="flex-1">
                <Text className="text-lg font-bold" style={{ color: colors.text }}>{team.name}</Text>
                {team.description && (
                  <Text className="text-sm" numberOfLines={1} style={{ color: colors.textSecondary }}>
                    {team.description}
                  </Text>
                )}
                <View className="flex-row items-center mt-1">
                  <Ionicons name="trophy" size={14} color={teamColor} />
                  <Text className="ml-1 font-semibold" style={{ color: teamColor }}>
                    {team.total_score} punten
                  </Text>
                </View>
              </View>

              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
            </Pressable>
          </View>
        )}

        {/* Action Buttons */}
        {!isOwnProfile && (
          <View className="px-4 mb-6">
            {/* Chat Button */}
            <Pressable
              onPress={handleStartChat}
              className="bg-primary-600 rounded-2xl py-4 flex-row items-center justify-center active:bg-primary-700 mb-3"
              accessibilityRole="button"
              accessibilityLabel={`Start chat met ${profile.full_name}`}
            >
              <Ionicons name="chatbubble-ellipses" size={22} color="#FFFFFF" />
              <Text className="text-white font-bold text-lg ml-2">
                Start een chat
              </Text>
            </Pressable>

            {/* Call Button */}
            {profile.phone && (
              <Pressable
                onPress={handleCall}
                className="bg-green-600 rounded-2xl py-4 flex-row items-center justify-center active:bg-green-700"
                accessibilityRole="button"
                accessibilityLabel={`Bel ${profile.full_name}`}
              >
                <Ionicons name="call" size={22} color="#FFFFFF" />
                <Text className="text-white font-bold text-lg ml-2">
                  Bellen
                </Text>
              </Pressable>
            )}
          </View>
        )}

        {/* Personal Info */}
        <View className="px-4 mb-6">
          <Text className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: colors.textSecondary }}>
            Persoonlijke Info
          </Text>
          <View className="rounded-2xl px-4" style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}>
            {/* Birthday */}
            {profile.date_of_birth && (
              <>
                <InfoRow
                  icon="gift-outline"
                  label="Verjaardag"
                  value={new Date(profile.date_of_birth).toLocaleDateString("nl-NL", {
                    day: "numeric",
                    month: "long",
                  })}
                />
                <View className="h-px" style={{ backgroundColor: colors.border }} />
              </>
            )}

            {/* Phone */}
            {profile.phone && (
              <>
                <InfoRow
                  icon="call-outline"
                  label="Telefoon"
                  value={profile.phone}
                  onPress={!isOwnProfile ? handleCall : undefined}
                />
                <View className="h-px" style={{ backgroundColor: colors.border }} />
              </>
            )}

            {/* Email */}
            {profile.email && (
              <InfoRow
                icon="mail-outline"
                label="Email"
                value={profile.email}
                onPress={!isOwnProfile ? handleEmail : undefined}
              />
            )}
          </View>
        </View>

        {/* Member Since */}
        <View className="px-4 mb-8 items-center">
          <Text className="text-sm" style={{ color: colors.textTertiary }}>
            Lid sinds{" "}
            {new Date(profile.created_at).toLocaleDateString("nl-NL", {
              month: "long",
              year: "numeric",
            })}
          </Text>
        </View>

        <View className="h-4" />
      </ScrollView>
    </SafeAreaView>
  );
}

